import SendIcon from "@mui/icons-material/SendRounded";
import { Button, ListItem, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AutocompleteCreatable from "@entrilia/shared/components/AutocompleteCreatable";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import { combineValidators, emailValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";
import type { TestInvitationContact } from "../../../../api/types/investorPortalTypes";
import useUserContext from "../../../../context/useUserContext";

interface Props {
  testInvitationContacts: TestInvitationContact[];
  disabled?: boolean;
}

const sendTestInvitationEmail = withErrorHandling(adminApi.sendTestInvitationEmail);

const validateEmail = combineValidators(requiredValidator, emailValidator);

const SendTestInvitationEmail = ({ testInvitationContacts, disabled }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { email: currentUserEmail } = useUserContext();

  const [testEmailAddress, setTestEmailAddress] = useState(currentUserEmail);
  const [selectedContact, setSelectedContact] = useState<TestInvitationContact>();
  const [isSending, setIsSending] = useState(false);

  const handleButtonClick = async () => {
    if (!selectedContact) {
      return;
    }

    setIsSending(true);

    const sanitizedEmail = sanitizeEmail(testEmailAddress);
    const [, error] = await sendTestInvitationEmail({
      recipientEmail: sanitizedEmail,
      contactId: selectedContact.id,
    });

    setIsSending(false);

    if (error) {
      logError(error, "[SendTestInvitationEmail]");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(`Test invitation email sent to ${sanitizedEmail}`);
  };

  const { isValid: isEmailValid } = validateEmail(sanitizeEmail(testEmailAddress));

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Typography variant="subtitle1">Test Email</Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          variant="outlined"
          label="Email"
          value={testEmailAddress}
          onChange={({ target }) => setTestEmailAddress(target.value)}
        />

        <AutocompleteCreatable
          withLazyRendering
          noItemsText="No contacts found"
          openOnFocus
          fullWidth
          options={testInvitationContacts}
          getOptionLabel={(o) => `${o.fullName} <${o.email}>`}
          renderOption={({ key: _key, ...props }, option) => (
            <ListItem key={option.id} {...props}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">{option.fullName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {option.email}
                </Typography>
              </Stack>
            </ListItem>
          )}
          isOptionEqualToValue={(opt, val) => opt.email === val.email}
          renderInput={(params) => <TextField {...params} label="Preview invitation as" />}
          value={selectedContact ?? null}
          onChange={(_, newValue) => {
            setSelectedContact(newValue ?? undefined);
          }}
        />
      </Stack>
      <Button
        variant="outlined"
        color="primary"
        loading={isSending}
        endIcon={<SendIcon />}
        disabled={!!disabled || !isEmailValid || !selectedContact}
        onClick={handleButtonClick}
        sx={{ width: "7.5rem" }}
      >
        Send Email
      </Button>
    </Stack>
  );
};

export default SendTestInvitationEmail;
